import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement } from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import AnalyticsDetailLayout from './AnalyticsDetailLayout';
import AnalyticsInfoHint from './AnalyticsInfoHint';
import { kpiHelp } from './analyticsKpiDefinitions.js';
import {
  PerformanceOfficialKpis,
  PerformanceScopeBanner,
  OpsRegistreBanner,
} from '../AnalyticsRegistreBanner';
import {
  Euro,
  ShoppingCart,
  BarChart3,
  RotateCcw,
  Eye,
  Users,
  Gift,
  Package,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement);

const DETAIL_ICONS = {
  ca: Euro,
  commandes: ShoppingCart,
  panier: BarChart3,
  remboursements: RotateCcw,
  trafic: Eye,
  clients: Users,
  utilisateurs: Users,
  gift: Gift,
  comportement: Users,
  produits: Package,
};

const DETAIL_GRADIENTS = {
  ca: 'from-emerald-600 to-teal-700',
  commandes: 'from-blue-600 to-indigo-700',
  panier: 'from-violet-600 to-purple-700',
  remboursements: 'from-orange-500 to-amber-700',
  trafic: 'from-sky-600 to-cyan-700',
  clients: 'from-indigo-600 to-slate-700',
  utilisateurs: 'from-indigo-600 to-slate-700',
  gift: 'from-pink-600 to-rose-700',
  comportement: 'from-indigo-600 to-slate-700',
  produits: 'from-slate-700 to-indigo-800',
};

const DONUT_COLORS = ['#2563eb', '#f97316', '#10b981', '#8b5cf6', '#64748b', '#ef4444'];

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const fmtEuro = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));

function EvolutionLineChart({ series }) {
  if (!series?.points?.length) return null;
  return (
    <div className="h-56" data-testid="analytics-evolution-chart">
      <Line
        data={{
          labels: series.points.map((p) => {
            const raw = String(p.date || '');
            const parsed =
              raw.length >= 10 ? new Date(`${raw.slice(0, 10)}T12:00:00`) : new Date(p.date);
            return Number.isNaN(parsed.getTime())
              ? raw
              : parsed.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
          }),
          datasets: [
            {
              label: series.label || 'Évolution',
              data: series.points.map((p) => p.value),
              borderColor: series.color || '#059669',
              backgroundColor: 'rgba(5,150,105,0.12)',
              tension: 0.25,
              fill: true,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true } },
        }}
      />
    </div>
  );
}

/**
 * Rendu page détail Analytics commerçant — Overview intact.
 */
export default function AnalyticsDetailView({
  model,
  onBack,
  evolutionChart = null,
  performanceOverview = null,
  merchantName = null,
  detailKey = null,
  periodControls = null,
}) {
  const navigate = useNavigate();
  if (!model) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Détail indisponible pour cette métrique.
        <button type="button" className="ml-2 underline" onClick={onBack}>
          Retour Analytics
        </button>
      </div>
    );
  }

  const detail = model.detail;
  const showPerfBanner =
    !model.hidePerformanceBanner && ['panier', 'ca', 'commandes'].includes(detailKey);

  let detailBody = null;

  if (detail) {
    const canalPie = detail.canalPie || [];
    detailBody = (
      <div className="space-y-6">
        {canalPie.length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5">
              Répartition des commandes par canal
              <AnalyticsInfoHint text="Même périmètre que le tableau Performance par canal : commandes payées ventilées Click & Collect, livraison, caisse…" />
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="h-56 max-w-xs mx-auto w-full" data-testid="analytics-orders-channel-pie">
                <Pie
                  data={{
                    labels: canalPie.map((s) => s.label),
                    datasets: [
                      {
                        data: canalPie.map((s) => s.count),
                        backgroundColor: canalPie.map((_, i) => DONUT_COLORS[i % DONUT_COLORS.length]),
                        borderWidth: 1,
                        borderColor: '#fff',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            const row = canalPie[ctx.dataIndex];
                            return `${row?.label}: ${row?.count} commandes (${row?.pct}%)`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
              <ul className="space-y-2 text-sm">
                {canalPie.map((s) => (
                  <li
                    key={s.key || s.label}
                    className="flex justify-between gap-3 border-b border-gray-50 pb-1.5"
                  >
                    <span className="text-gray-800">{s.label}</span>
                    <span className="font-medium tabular-nums shrink-0">
                      {s.count} commandes · {s.pct}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        {(detail.canaux || []).length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
              Performance par canal
              <AnalyticsInfoHint text="CA, commandes et panier moyen par mode de vente (Click & Collect, livraison, caisse…)." />
            </h3>
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Canal</th>
                    <th className="px-3 py-2 font-medium text-right">Commandes</th>
                    <th className="px-3 py-2 font-medium text-right">CA</th>
                    <th className="px-3 py-2 font-medium text-right">Panier moyen</th>
                    <th className="px-3 py-2 font-medium text-right">Part du CA</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detail.canaux.map((c) => {
                    const cmd = Number(c.commandes || 0);
                    const ca = Number(c.ca || 0);
                    const totalCa = detail.canaux.reduce((s, x) => s + Number(x.ca || 0), 0);
                    const part = totalCa > 0 ? Math.round((ca / totalCa) * 1000) / 10 : null;
                    return (
                      <tr key={c.key || c.label}>
                        <td className="px-3 py-2">{c.label || c.key}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{cmd}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtEuro(ca)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {cmd > 0 ? fmtEuro(ca / cmd) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {part != null ? `${part}%` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {(detail.canauxPanier || []).length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Panier moyen par canal</h3>
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
              {detail.canauxPanier.map((c) => (
                <li key={c.label} className="px-3 py-2.5 text-sm flex justify-between gap-3">
                  <span>{c.label}</span>
                  <span className="font-medium tabular-nums">
                    {fmtEuro(c.panier)} · {c.commandes} cmd
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {(detail.evolutionDays || []).length > 0 ? (
          <div data-testid="analytics-trafic-evolution-table">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Évolution des visites</h3>
            <div className="overflow-x-auto rounded-lg border border-gray-100 max-h-64 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-gray-500 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium text-right">Visites</th>
                    <th className="px-3 py-2 font-medium text-right">Pages vues</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detail.evolutionDays.map((d) => (
                    <tr key={d.date}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {new Date(`${d.date}T12:00:00`).toLocaleDateString('fr-FR', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                        })}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{d.sessions ?? 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{d.vues ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {(detail.categoriesConsulted || []).length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              Catégories les plus consultées
            </h3>
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
              {detail.categoriesConsulted.map((c) => (
                <li key={c.label} className="px-3 py-2 text-sm flex justify-between">
                  <span>{c.label}</span>
                  <span className="font-medium">{c.vues} vues</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {(detail.heuresPics || []).length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Heures de passage sur le site</h3>
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
              {detail.heuresPics.map((h) => (
                <li key={h.heure} className="px-3 py-2 text-sm flex justify-between">
                  <span>
                    {h.heure}h – {h.heure + 1}h
                  </span>
                  <span className="font-medium">{h.vues} vues</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {(detail.list || []).length > 0 && !canalPie.length ? (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
            {detail.list.map((item, idx) => (
              <li
                key={item.key || `${item.label}-${idx}`}
                className="px-3 py-2.5 text-sm flex justify-between gap-3"
              >
                <span className="text-gray-800">{item.label}</span>
                <span className="text-gray-600 shrink-0 font-medium">{item.value}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {!detail.hideGeo && (detail.geo || []).length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
              Origine géographique des pages vues
              <AnalyticsInfoHint text={kpiHelp('geoVisites')} />
            </h3>
            <p className="text-xs text-gray-500 mb-2">Pays · Pages vues</p>
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
              {detail.geo.map((g) => (
                <li key={g.pays || g.country} className="px-3 py-2 text-sm flex justify-between">
                  <span>{g.pays || g.country || 'Inconnu'}</span>
                  <span className="font-medium">
                    {g.pagesVues ?? g.visites ?? g.vues ?? 0} pages vues
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {(detail.funnelEtapes || []).length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
              Parcours d&apos;achat
              <AnalyticsInfoHint text="Nombre de sessions par étape. % de passage et d’abandon entre étapes successives." />
            </h3>
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
              {detail.funnelEtapes.map((etape, i) => {
                const prev = i > 0 ? detail.funnelEtapes[i - 1]?.sessions : etape.sessions;
                const pass =
                  i > 0 && prev > 0 ? Math.round((etape.sessions / prev) * 100) : null;
                const drop =
                  i > 0 && prev > 0
                    ? Math.round(((prev - etape.sessions) / prev) * 100)
                    : null;
                return (
                  <li key={etape.key || etape.label} className="px-3 py-2 text-sm flex justify-between gap-2">
                    <span>{etape.label}</span>
                    <span className="text-gray-600 shrink-0">
                      {etape.sessions} sessions
                      {pass != null ? ` · ${pass}% passage` : ''}
                      {drop != null && drop > 0 ? ` · ${drop}% abandon` : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {(detail.pointsAbandon || []).length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              Où les visiteurs quittent le parcours
            </h3>
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
              {detail.pointsAbandon.map((p) => (
                <li key={p.etape || p.label} className="px-3 py-2 text-sm flex justify-between">
                  <span>{p.label}</span>
                  <span className="font-medium">
                    {p.sessions} sessions{p.pct != null ? ` · ${p.pct}%` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {(detail.topRecherches || []).length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Recherches dans le menu</h3>
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Recherche</th>
                    <th className="px-3 py-2 font-medium text-right">Recherches</th>
                    <th className="px-3 py-2 font-medium text-right">Sessions</th>
                    <th className="px-3 py-2 font-medium text-right">Résultats moy.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detail.topRecherches.map((r) => (
                    <tr key={r.query}>
                      <td className="px-3 py-2">« {r.query} »</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.recherches}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.sessions}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {r.resultatsMoyens != null ? r.resultatsMoyens : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {(detail.topProduitsConsultes || detail.topProduits || []).length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Produits les plus consultés</h3>
            {(() => {
              const rows = detail.topProduitsConsultes || detail.topProduits;
              const showCat = rows.some((p) => p.categorie);
              return (
                <div className="overflow-x-auto rounded-lg border border-gray-100">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs text-gray-500">
                      <tr>
                        <th className="px-3 py-2 font-medium">Produit</th>
                        {showCat ? (
                          <th className="px-3 py-2 font-medium">Catégorie</th>
                        ) : null}
                        <th className="px-3 py-2 font-medium text-right">Vues</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rows.map((p) => (
                        <tr key={p.produitId || p.nom}>
                          <td className="px-3 py-2">{p.nom}</td>
                          {showCat ? (
                            <td className="px-3 py-2 text-gray-600">{p.categorie || '—'}</td>
                          ) : null}
                          <td className="px-3 py-2 text-right tabular-nums">{p.vues}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!showCat ? (
                    <p className="text-[11px] text-gray-500 px-3 py-2 border-t border-gray-100">
                      Catégorie non disponible sur cette API — colonne masquée (pas de tirets).
                    </p>
                  ) : null}
                </div>
              );
            })()}
          </div>
        ) : null}

        {(detail.categoriesCa || []).length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
              Performance par catégorie
              <AnalyticsInfoHint text="CA et quantités vendues regroupés par catégorie produit, sur les commandes payées." />
            </h3>
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Catégorie</th>
                    <th className="px-3 py-2 font-medium text-right">Quantité</th>
                    <th className="px-3 py-2 font-medium text-right">CA</th>
                    <th className="px-3 py-2 font-medium text-right">Part du CA</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detail.categoriesCa.map((c) => (
                    <tr key={c.categorie}>
                      <td className="px-3 py-2 font-medium">{c.categorie}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.quantite}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtEuro(c.ca)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.partCaPct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {(detail.produitsCa || detail.produitsQuantite || []).length > 0 ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
              Tableau des produits
              <AnalyticsInfoHint text="Tri disponible sur le dashboard. Ici : classement par CA (référence commerciale)." />
            </h3>
            {detail.periodHint ? (
              <p className="text-xs text-gray-500 mb-2">{detail.periodHint}</p>
            ) : null}
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Produit</th>
                    <th className="px-3 py-2 font-medium">Catégorie</th>
                    <th className="px-3 py-2 font-medium text-right">Quantité vendue</th>
                    <th className="px-3 py-2 font-medium text-right">CA</th>
                    <th className="px-3 py-2 font-medium text-right">Part du CA</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(detail.produitsCa || detail.produitsQuantite).map((p) => (
                    <tr key={p.produitId}>
                      <td className="px-3 py-2 font-medium">{p.nom}</td>
                      <td className="px-3 py-2 text-gray-600">{p.categorie || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.quantite}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtEuro(p.ca)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.partCaPct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {detail.clients != null ? (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
              Visiteurs identifiés
              <AnalyticsInfoHint text={kpiHelp('visiteursIdentifies')} />
            </h3>
            {!detail.identityAvailable || detail.clients.length === 0 ? (
              <p className="text-sm text-gray-500">
                Aucun visiteur identifié sur la période. Les visiteurs sans compte restent sans
                identité.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-100">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs text-gray-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">Nom</th>
                      <th className="px-3 py-2 font-medium">Email</th>
                      <th className="px-3 py-2 font-medium text-right">Visites</th>
                      <th className="px-3 py-2 font-medium text-right">Pages</th>
                      <th className="px-3 py-2 font-medium">Dernière visite</th>
                      <th className="px-3 py-2 font-medium text-right">Commandes (boutique)</th>
                      <th className="px-3 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {detail.clients.map((c) => {
                      const isClient =
                        c.estClientBoutique === true ||
                        Number(c.nbCommandes || 0) > 0 ||
                        Boolean(c.carteFidelite);
                      const name = [c.prenom, c.nom].filter(Boolean).join(' ') || 'Visiteur';
                      const clientHref =
                        isClient && detail.commercantId
                          ? `/clients/${c.userId}?commercantId=${detail.commercantId}`
                          : null;
                      const openRow = () => {
                        if (clientHref) navigate(clientHref);
                      };
                      return (
                        <tr
                          key={c.userId}
                          className={
                            clientHref
                              ? 'cursor-pointer hover:bg-violet-50/70 focus-within:bg-violet-50/70'
                              : ''
                          }
                          onClick={clientHref ? openRow : undefined}
                          onKeyDown={
                            clientHref
                              ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openRow();
                                  }
                                }
                              : undefined
                          }
                          tabIndex={clientHref ? 0 : undefined}
                          role={clientHref ? 'link' : undefined}
                          aria-label={
                            clientHref ? `Ouvrir la fiche client ${name}` : undefined
                          }
                        >
                          <td className="px-3 py-2 font-medium text-gray-900">{name}</td>
                          <td className="px-3 py-2 text-gray-600">{c.email || '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{c.visites ?? '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{c.pagesVues ?? '—'}</td>
                          <td className="px-3 py-2">{fmtDate(c.derniereVisite)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {c.nbCommandes != null ? c.nbCommandes : '—'}
                          </td>
                          <td className="px-3 py-2">
                            {clientHref ? (
                              <Link
                                to={clientHref}
                                className="text-violet-700 hover:underline text-xs font-medium"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Fiche client →
                              </Link>
                            ) : (
                              <span className="text-[11px] text-gray-500">
                                Visiteur (pas encore client boutique)
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

        {detail.giftCardList === null && detailKey === 'gift' ? (
          <p className="text-xs text-gray-500">
            Liste détaillée des cartes : à venir lorsque l’export carte par carte sera branché —
            les totaux ci-dessus restent certifiés.
          </p>
        ) : null}
      </div>
    );
  }

  const seriesChart = model.evolutionSeries?.points?.length ? (
    <EvolutionLineChart series={model.evolutionSeries} />
  ) : evolutionChart;
  const evolutionBlock = seriesChart || null;
  const lead =
    showPerfBanner && performanceOverview ? (
      <div className="space-y-3">
        <PerformanceScopeBanner
          resolver={performanceOverview.resolver}
          periode={performanceOverview.periode}
          merchantFriendly
        />
        <PerformanceOfficialKpis
          performanceOverview={performanceOverview}
          merchantFriendly
        />
        {detailKey === 'commandes' ? <OpsRegistreBanner /> : null}
      </div>
    ) : null;

  return (
    <AnalyticsDetailLayout
      title={model.title}
      subtitle="Analyse approfondie Analytics"
      merchantName={merchantName}
      definition={model.definition}
      periodLabel={model.periodLabel}
      mainValue={model.mainValue}
      mainSuffix={model.mainSuffix}
      deltaLabel={model.deltaLabel}
      gradient={DETAIL_GRADIENTS[detailKey] || 'from-slate-600 to-slate-800'}
      icon={DETAIL_ICONS[detailKey] || null}
      onBack={onBack}
      periodControls={periodControls}
      understand={model.understand}
      lead={lead}
      evolution={evolutionBlock}
      detail={
        detail
          ? {
              title: detail.title || 'Détail',
              body: detailBody,
            }
          : null
      }
      actionSoon={model.actionSoon}
    />
  );
}
